export type User = { username: string; role: string };

export type TabCounts = {
  total: number;
  unprocessed: number;
  in_progress: number;
  completed: number;
};

export type ApplicationSummary = {
  id: string;
  customer_name: string;
  customer_age: number;
  product: string;
  product_label: string;
  amount_cents: number;
  amount_display: string;
  stage: string;
  status: string;
  status_label: string;
  risk_tier: string | null;
  submitted_at: string;
  updated_at: string;
  submitted_ago: string;
  is_finalized: boolean;
};

export type CustomerDTO = {
  full_name: string;
  dob: string;
  age: number;
  email: string;
  address: string;
  employer: string;
  job_title: string;
  employment_years: number;
  id_channel: string;
  id_verified_at: string;
};

export type ApplicationDetail = {
  id: string;
  product: string;
  product_label: string;
  amount_cents: number;
  amount_display: string;
  term_months: number;
  purpose: string;
  stated_income_cents: number;
  stage: string;
  status: string;
  status_label: string;
  risk_tier: string | null;
  submitted_at: string;
  decided_at: string | null;
  decision_reason: string | null;
  officer_discretion_used: boolean;
  officer_justification: string | null;
  customer: CustomerDTO;
};

export type DocumentDTO = {
  id: string;
  doc_type: string;
  doc_type_label: string;
  filename: string;
  pages: number;
  upload_source: string;
  processed_at: string;
};

export type ExtractedFieldDTO = {
  id: string;
  label: string;
  key: string;
  value: string;
  confidence: number;
  source_doc_id?: string;
  source_doc_type?: string;
  source_filename?: string;
  source_page?: number;
  cross_check_status?: string | null;
};

export type RiskFindingDTO = {
  id: string;
  title: string;
  severity: string;
  description: string;
  tier_before: string | null;
  tier_after: string | null;
  citation_anchor?: string;
  citation_title?: string;
  citation_markdown?: string;
};

export type PolicyEvaluationDTO = {
  id: string;
  policy_code: string;
  policy_name: string;
  policy_description: string;
  result: "pass" | "warn" | "fail";
  details: string;
  citation?: string;
  citation_anchor?: string;
  citation_title?: string;
  citation_markdown?: string;
  discretion_eligible: boolean;
  discretion_bounds?: Record<string, unknown> | null;
};

export type JourneyEventDTO = {
  id: string;
  stage: string;
  agent_name: string;
  phase: string;
  message: string;
  payload?: unknown;
  started_at: string;
  completed_at?: string | null;
  duration_ms: number;
};

export type AuditEntryDTO = {
  id: string;
  actor: string;
  action: string;
  details?: unknown;
  at: string;
};

export type PolicyManualSection = {
  id: string;
  anchor: string;
  title: string;
  body_markdown: string;
  section_order: number;
};

export type IntakePayload = {
  documents: DocumentDTO[];
  extracted_fields: ExtractedFieldDTO[];
  journey_events: JourneyEventDTO[];
};

export type AnalysisPayload = {
  reached: boolean;
  risk_tier: string | null;
  findings: RiskFindingDTO[];
  journey_events: JourneyEventDTO[];
  extracted_fields: ExtractedFieldDTO[];
};

export type CoordinationPayload = {
  reached: boolean;
  evaluations: PolicyEvaluationDTO[];
  journey_events: JourneyEventDTO[];
};

export type DecisionPayload = {
  reached: boolean;
  detail: ApplicationDetail;
  journey_events: JourneyEventDTO[];
  audit: AuditEntryDTO[];
};

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    throw new APIError(res.status, msg || res.statusText);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

export class APIError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export const api = {
  login: (username: string, password: string) =>
    req<User>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    }),
  logout: () => req<{ status: string }>("/api/auth/logout", { method: "POST" }),
  me: () => req<User>("/api/auth/me"),

  listApplications: (tab: string, q: string) =>
    req<{ items: ApplicationSummary[]; counts: TabCounts }>(
      `/api/applications?tab=${encodeURIComponent(tab)}&q=${encodeURIComponent(q)}`,
    ),
  getApplication: (id: string) => req<ApplicationDetail>(`/api/applications/${id}`),
  getIntake: (id: string) => req<IntakePayload>(`/api/applications/${id}/intake`),
  getAnalysis: (id: string) => req<AnalysisPayload>(`/api/applications/${id}/analysis`),
  getCoordination: (id: string) => req<CoordinationPayload>(`/api/applications/${id}/coordination`),
  getDecision: (id: string) => req<DecisionPayload>(`/api/applications/${id}/decision`),
  advance: (id: string) => req<{ status: string }>(`/api/applications/${id}/advance`, { method: "POST" }),
  reRunStage: (id: string, stage: string) =>
    req<{ status: string }>(`/api/applications/${id}/stages/${stage}/run`, { method: "POST" }),
  submitDecision: (id: string, action: string, justification: string) =>
    req<{ status: string }>(`/api/applications/${id}/decision`, {
      method: "POST",
      body: JSON.stringify({ action, justification }),
    }),

  getPolicyManual: () => req<{ sections: PolicyManualSection[] }>(`/api/policy-manual`),
  getStats: () => req<TabCounts>(`/api/admin/stats`),
  resetDB: () => req<{ status: string }>(`/api/admin/reset-db`, { method: "POST" }),
  tickAll: () => req<{ status: string }>(`/api/admin/tick-all`, { method: "POST" }),
};
